// SYNC WITH FRONTEND ORDER-SEAT-PICKER

import React from "react";
import { colors as Colors } from "material-ui/styles";
import { RaisedButton, FlatButton } from "material-ui";

import { formatPrice } from "../../util";

// CHANGED IMPORT PATH FROM SYNC
import OrderSeatList from "./seat-list.jsx";

const SEATS_IO_SCRIPT_URL = "https://cdn.seatsio.net/chart.js";

class OrderSeatPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showBestAvailableSelector: false,
      editorInstance: null,
      selectedSeats: [],
      badSeats: []
    };

    this._makePricing = this._makePricing.bind(this);
    this.createInstance = this.createInstance.bind(this);
    this.destroyInstance = this.destroyInstance.bind(this);
    this.submit = this.submit.bind(this);
  }

  _makePricing() {
    let { event } = this.props,
      { venue } = event;

    let pricing = {};

    event.ticket_buckets.forEach(b => {
      if (b.reserved_seating_category) {
        if (!pricing[b.reserved_seating_category]) {
          pricing[b.reserved_seating_category] = [];
        }
        pricing[b.reserved_seating_category].push({
          ticketType: b.name,
          price: b.price,
          ticket_bucket: b.id
        });

        if (b.variants) {
          b.variants.forEach(v => {
            pricing[b.reserved_seating_category].push({
              ticketType: `${b.name} – ${v.name}`,
              price: v.price,
              ticket_bucket: b.id,
              variant: v.id
            });
          });
        }
      }
    });

    return Object.keys(pricing).map(key => {
      // Only 1 ticket type for this section
      if (pricing[key].length === 1) {
        return {
          category: key,
          price: pricing[key][0].price,
          ticket_bucket: pricing[key][0].ticket_bucket
        };
      }

      return {
        category: key,
        ticketTypes: pricing[key]
      };
    });
  }

  createInstance() {
    let { event, order } = this.props,
      { venue } = event;

    if (!window.seatsio || !venue || this.state.editorInstance) {
      return;
    }

    let instance = new window.seatsio.SeatingChart({
      divId: "seatioInject",
      publicKey: process.env.SEATS_IO_PUBLIC_KEY,
      event: event.id,
      pricing: this._makePricing(),
      priceFormatter: p => formatPrice(p, /* short */ true),
      priceLevelsTooltipMessage: "Select ticket type:",
      reserveOnSelect: false, // this causes too many problems...
      multiSelectEnabled: true,
      maxSelectedObjects: this.props.quantityAvailable,
      baseColor: "#0288d1",
      selectionValidators: [{ type: "noOrphanSeats", enabled: false }],
      messages: {
        maxSelectionReached: "This seat is not available"
      },
      onObjectSelected: (object, ticketType) => {
        let { selectedSeats } = this.state;

        selectedSeats.push({
          ticket_bucket: event.ticket_buckets.find(
            b =>
              b.reserved_seating_category === object.category.key.toString() &&
              (!ticketType || b.id === ticketType.ticket_bucket)
          ),
          seat: object.label,
          seatmap_object: object,
          variant: ticketType ? ticketType.variant : undefined
        });

        this.setState({ selectedSeats }, () => {
          if (this.seatList) {
            this.seatList.scrollIntoView();
          }
        });
      },
      onObjectDeselected: (object, ticketType) => {
        let { selectedSeats } = this.state;
        selectedSeats.splice(
          this.state.selectedSeats.findIndex(s => s.seat === object.label),
          1
        );
        this.setState({ selectedSeats });
      },
      onSelectedObjectBooked: (object, ticketType) => {
        this.setState({
          badSeats: this.state.badSeats.concat(object.label)
        });
      },
      onScrolledOutOfBoundsVertically: amount => {
        // This is hacky - basically trasmit the scroll of the seatmap --> overlay,
        // to help for scrolling on mobile.
        let roots = document.querySelectorAll("div > [data-reactroot]");
        roots[roots.length - 1].scrollTop += amount;
      },
      objectIcon: object => {
        if (object.selected) {
          return "check";
        }
        return null;
      },
      onChartRendered: () => {
        // Re-select seats if specified in the order
        let currentSeats = order.subitems.reduce(
          (all, s) =>
            all.concat(
              s.seats.map(id => {
                // Determine the proper variant selected - note this is a bit hacky, since we have to pass in
                // a string key for the variant, so there's some backwardsness to generate the right key.
                const ticketBucket = event.ticket_buckets.find(
                  b => b.id === s.ticket_bucket
                );
                const ticketType = ticketBucket.variants.length
                  ? s.variant
                    ? `${ticketBucket.name} – ${
                        ticketBucket.variants.find(v => v.id === s.variant).name
                      }`
                    : `${ticketBucket.name}`
                  : undefined;
                return {
                  id,
                  ticketType
                };
              }) || []
            ),
          []
        );
        if (currentSeats.length) {
          instance.selectObjects(currentSeats);
        }
      }
    });

    this.setState({
      editorInstance: instance
    });

    instance.render();
  }

  destroyInstance() {
    document.getElementById("seatioInject").innerHTML = "";
    window.seatsio.removeFromArray(
      this.state.editorInstance,
      window.seatsio.charts
    );
  }

  submit() {
    let ticketBuckets = this.state.selectedSeats.reduce(function (
      byBucket,
      seat
    ) {
      const key = `${seat.ticket_bucket.id}-${seat.variant || ""}`;
      if (byBucket[key]) {
        byBucket[key].push(seat.seat);
      } else {
        byBucket[key] = [seat.seat];
      }
      return byBucket;
    },
    {});

    this.props.updateCurrentOrder({
      seats_confirmed: true,
      subitems: Object.entries(ticketBuckets).map(([identifier, seats]) => {
        const [id, variant] = identifier.split("-");
        return {
          ticket_bucket: id,
          variant: variant,
          quantity: seats.length,
          seats: seats
        };
      })
    });
  }

  componentDidMount() {
    if (!document.querySelector(`script[src="${SEATS_IO_SCRIPT_URL}"]`)) {
      var script = document.createElement("script");
      script.type = "text/javascript";
      script.src = SEATS_IO_SCRIPT_URL;
      script.onload = this.createInstance;
      document.body.appendChild(script);
    }
    this.createInstance();
  }

  componentWillUnmount() {
    if (this.state.editorInstance) {
      this.destroyInstance();
    }
  }

  getStyles() {
    return {
      error: {
        color: "#fff",
        backgroundColor: Colors.red600,
        padding: 10
      },
      inner: {
        width: "100%",
        height: 400,
        position: "relative",
        marginBottom: 16
      },
      innerBestAvailableShown: {
        display: "none"
      },
      actions: {
        marginTop: 20
      }
    };
  }

  render() {
    let styles = this.getStyles(),
      currentBadTickets = this.state.selectedSeats.filter(
        s => this.state.badSeats.indexOf(s.seat) !== -1
      );

    return (
      <div>
        {currentBadTickets.length ? (
          <div style={styles.error}>
            <div>
              Another customer has reserved the following seat
              {currentBadTickets.length !== 1 ? "s" : ""}:{" "}
              <strong>{currentBadTickets.map(t => t.seat).join(",")}</strong>
            </div>
            <div>
              Please remove {currentBadTickets.length !== 1 ? "them" : "it"}{" "}
              from your purchase before continuing.
            </div>
          </div>
        ) : null}
        {this.state.showBestAvailableSelector ? (
          <div>What are you looking for?</div>
        ) : null}
        <div
          id="seatioInject"
          style={Object.assign(
            {},
            styles.inner,
            this.state.showBestAvailableSelector &&
              styles.innerBestAvailableShown
          )}
        />
        <OrderSeatList
          containerRef={r => {
            this.seatList = r;
          }}
          seats={this.state.selectedSeats}
          event={this.props.event}
          onRemoveSeat={id => this.state.editorInstance.deselectObjects([id])}
        />
        <div style={styles.actions}>
          <FlatButton
            label="Cancel"
            onClick={() => {
              this.props.onCancel();
            }}
            style={{ float: "left" }}
          />
          <RaisedButton
            label={`Continue (${this.state.selectedSeats.length} seat${
              this.state.selectedSeats.length === 1 ? "" : "s"
            })`}
            primary={true}
            disabled={
              this.state.selectedSeats.length === 0 ||
              currentBadTickets.length > 0
            }
            onClick={this.submit}
            style={{ float: "right" }}
          />
        </div>
      </div>
    );
  }
}

export default OrderSeatPicker;



// WEBPACK FOOTER //
// ./src/app/components/partials/seat-picker.jsx